'use client';

import { User } from '@phosphor-icons/react/ssr';
import {
  Children,
  type ComponentProps,
  createContext,
  type ElementType,
  isValidElement,
  type ReactNode,
  useContext,
} from 'react';

import {
  Avatar as AvatarRoot,
  AvatarFallback,
  avatarGroupVariants,
  AvatarImage,
  avatarVariants,
} from '../ui/avatar';

export type AvatarSize = 20 | 24 | 32 | 40 | 56 | 80;
export type AvatarGroupSize = 24 | 32 | 40;
export type AvatarStatus = 'online' | 'away' | 'busy';

const STATUS_LABEL: Record<AvatarStatus, string> = {
  online: 'Online',
  away: 'Away',
  busy: 'Busy',
};

/** Set by AvatarGroup: the group's size, and the separating ring. */
const AvatarGroupContext = createContext<{ size: AvatarGroupSize } | null>(null);

/** "Maya Kim" → "MK", "reelplicate" → "R". */
export function getInitials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters =
    words.length > 1 ? [words[0]![0], words[words.length - 1]![0]] : [words[0]?.[0]];
  return letters.join('').toUpperCase();
}

export type AvatarProps = Omit<ComponentProps<'span'>, 'children'> & {
  /** Figma Type=Image: the photo. While it loads, or if it fails, the initials (or icon) show. */
  src?: string;
  /** The person or workspace. Gives the initials and, by default, the accessible name. */
  name?: string;
  /** Figma "Initials" (1–2 letters). Defaults to the initials of `name`. */
  initials?: string;
  /**
   * Accessible name. Defaults to `name`. Pass `""` when the name is already
   * next to the avatar - the avatar is then hidden from assistive tech.
   */
  alt?: string;
  /** Figma "Size": 20 · 24 · 32 (default) · 40 · 56 · 80. Inside an AvatarGroup, the group's size. */
  size?: AvatarSize;
  /** Figma "Status" dot: online (success), away (warning), busy (danger). Added to the accessible name. */
  status?: AvatarStatus;
  /** Figma Type=Icon: the icon when there's no name. Defaults to Icon/user. */
  icon?: ElementType<{ className?: string }>;
};

/**
 * A person or workspace (Figma: Components ↳ Avatar). Shows the photo
 * (`src`), falling back to initials (`name` / `initials`), then to the user
 * icon. Built on the Radix Avatar via the shadcn primitive. Always pair it
 * with a visible name or a Tooltip.
 */
export function Avatar({
  src,
  name,
  initials,
  alt,
  size: sizeProp,
  status,
  icon: Icon = User,
  className,
  ...props
}: AvatarProps) {
  const group = useContext(AvatarGroupContext);
  const size = group?.size ?? sizeProp ?? 32;
  const letters = initials ?? (name ? getInitials(name) : '');
  const styles = avatarVariants({
    size,
    fallback: letters ? 'initials' : 'icon',
    status,
    grouped: Boolean(group),
  });
  const label = alt ?? name;
  const accessibleName =
    label && status ? `${label} (${STATUS_LABEL[status]})` : label || undefined;

  return (
    <AvatarRoot
      role={accessibleName ? 'img' : undefined}
      aria-label={accessibleName}
      aria-hidden={accessibleName ? undefined : true}
      data-size={size}
      className={styles.root({ className })}
      {...props}
    >
      <span data-slot="avatar-base" className={styles.base()}>
        {src && <AvatarImage src={src} alt="" className={styles.image()} />}
        <AvatarFallback className={styles.fallback()}>
          {letters || (
            <Icon aria-hidden data-slot="avatar-icon" className={styles.icon()} />
          )}
        </AvatarFallback>
      </span>
      {status && (
        <span aria-hidden data-slot="avatar-status" className={styles.status()} />
      )}
    </AvatarRoot>
  );
}

export type AvatarGroupProps = ComponentProps<'div'> & {
  /** Figma "Size": 24 · 32 (default) · 40. Applied to every Avatar inside. */
  size?: AvatarGroupSize;
  /** Most avatars to show before the "+N" overflow (Figma: max 4). */
  max?: number;
  /** Total people, when more exist than the Avatars passed. Defaults to the number of children. */
  total?: number;
  /** The Avatars. */
  children?: ReactNode;
};

/**
 * A stack of collaborators (Figma: Components ↳ Avatar Group). Shows up to
 * `max` avatars and a "+N" overflow count; a 2px surface ring separates the
 * overlaps and later avatars sit underneath. Name the group with
 * `aria-label` (e.g. "Collaborators").
 */
export function AvatarGroup({
  size = 32,
  max = 4,
  total,
  className,
  children,
  ...props
}: AvatarGroupProps) {
  const items = Children.toArray(children);
  const shown = items.slice(0, max);
  const overflow = (total ?? items.length) - shown.length;
  const styles = avatarGroupVariants({ size });

  return (
    <AvatarGroupContext.Provider value={{ size }}>
      <div
        role="group"
        data-slot="avatar-group"
        className={styles.root({ className })}
        {...props}
      >
        {shown.map((child, i) => (
          <span
            key={isValidElement(child) ? child.key : i}
            data-slot="avatar-group-item"
            className={styles.item()}
            style={{ zIndex: shown.length + 1 - i }}
          >
            {child}
          </span>
        ))}
        {overflow > 0 && (
          <span
            role="img"
            aria-label={`${overflow} more`}
            data-slot="avatar-group-overflow"
            className={styles.overflow()}
          >
            +{overflow}
          </span>
        )}
      </div>
    </AvatarGroupContext.Provider>
  );
}
